import axios from 'axios';
import { API_HOST } from '../consts';
import debounce from 'debounce-promise';

export const fetchCurricula = (params) =>
  axios
    .get(`${API_HOST}/curricula/curricula`, { params })
    .then((response) => response.data);

export const fetchCurriculaOptions = (params) =>
  axios.get(`${API_HOST}/curricula/curricula`, { params }).then((response) =>
    response.data.map((d) => ({
      value: d.id,
      label: `${d.name} - ${d.course_name}`,
    }))
  );
  
export const fetchTurma = async (params = {}) => {
  const { fetch_all: fetchAll, ...requestParams } = params;

  if (!fetchAll) {
    const response = await axios.get(`${API_HOST}/academico/turmas`, {
      params: requestParams,
    });
    return response.data.results;
  }

  let nextUrl = `${API_HOST}/academico/turmas`;
  let nextParams = requestParams;
  const allResults = [];

  while (nextUrl) {
    const response = await axios.get(nextUrl, { params: nextParams });
    allResults.push(...(response.data.results || []));
    nextUrl = response.data.next;
    nextParams = undefined;
  }

  return allResults;
};

export const updateStudentCurriculumContent = async (id, params) => {
  const res = await axios.put(
    `${API_HOST}/curricula/student_curriculum/${id}`,
    params
  );
  return res.data;
};

export const updateObservationTextContent = async (id, params) => {
  const res = await axios.patch(
    `${API_HOST}/curricula/student_curriculum/${id}/update_observation_field`,
    params
  );
  return res.data;
};

export const fetchPaginatedCurricula = (params) =>
  axios
    .get(`${API_HOST}/curricula/curricula/paginated`, { params })
    .then((response) => response.data);

export const fetchPaginatedCurriculaCards = (params) =>
  axios
    .get(`${API_HOST}/curricula/curricula/cards_content`, { params })
    .then((response) => response.data);

export const fetchStudentCurriculumLookup = debounce(
  (params) =>
    axios
      .get(`${API_HOST}/curricula/student_curriculum/lookup`, { params })
      .then((response) => response.data.results),
  500
);

export const fetchStudentCurriculum = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum`, { params })
    .then((response) => response.data);

export const fetchStudentCurriculumById = (id) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/${id}`)
    .then((response) => response.data);

export const fetchStudentCurriculumDetails = (id) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/${id}/details`)
    .then((response) => response.data);

export const fetchStudentCurriculumWithCPF = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/with_cpf`, { params })
    .then((response) => response.data);

export const fetchStudentCurriculaFichaAluno = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/ficha_aluno`, { params })
    .then((response) => response.data);

export const fetchStudentCurriculaSupplementaryHours = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/supplementary_hours`, {
      params,
    })
    .then((response) => response.data);

export const fetchStudentCurriculumStatus = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum_status`, { params })
    .then((response) =>
      response.data.map((d) => ({
        value: d.id,
        label: d.name,
      }))
    );

export const getStudentCurriculumStatuses = () =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum_status`)
    .then((response) =>
      response.data.map((d) => ({ id: d.id, name: d.name }))
    );

export const fetchStudentDocuments = (id) =>
  axios
    .get(`${API_HOST}/curricula/pending_documents/${id}`)
    .then((response) => response.data)
    .catch((error) => error.status);

export const fetchCertificate = (params) =>
  axios
    .get(`${API_HOST}/academico/check_has_academic_degree`, { params })
    .then((response) => response.data)
    .catch((error) => error.status);

export const retrieveCurricula = (id) =>
  axios
    .get(`${API_HOST}/curricula/curricula/${id}/detailed`)
    .then((response) => response.data);

export const fetchCurriculoFromCourse = (course) =>
  axios
    .get(`${API_HOST}/curricula/curricula/from_course`, { params: { course } })
    .then((response) => response.data);

export const fetchCurriculoContent = async (id) => {
  const response = await axios.get(
    `${API_HOST}/curricula/curricula/${id}/content`
  );
  return response.data;
};

export const createCurriculum = (params) =>
  axios
    .post(`${API_HOST}/curricula/curricula`, { ...params })
    .then((response) => response.data);

export const updateCurriculum = (data, id) =>
  axios
    .patch(`${API_HOST}/curricula/curricula/${id}`, data)
    .then((response) => response.data);

export const duplicateCurriculum = (id, params) =>
  axios
    .post(`${API_HOST}/curricula/curricula/${id}/duplicate_curriculum`, {
      ...params,
    })
    .then((response) => response.data)
    .catch((err) => {
      throw err;
    });

export const filterStudentCurriculum = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/enturmacao_em_massa`, {
      params,
    })
    .then((res) => res.data)
    .catch((err) => {
      throw err;
    });

export const fetchPaginatedStudentCurriculum = (params) =>
  axios
    .get(`${API_HOST}/curricula/student_curriculum/paginated`, {
      params,
    })
    .then((response) => response.data);

export const fetchPaginatedStudentCurriculumOptions = debounce(
  (params) =>
    axios
      .get(`${API_HOST}/curricula/student_curriculum/paginated`, {
        params,
      })
      .then((response) => response.data.results),
  500
);

export const verifyEnrollmentTicketData = (data) =>
  axios
    .post(
      `${API_HOST}/tickets/tickets/verify_disciplines_details_to_enroll`,
      data
    )
    .then((res) => res.data);

export const detailAvailableByCurriculum = async (params) => {
  const res = await axios.get(
    `${API_HOST}/academico/disciplinas/available_disciplines`,
    { params }
  );
  return res.data.results;
};

export const editDispensaDisciplina = (id, data) =>
  axios
    .put(`${API_HOST}/curricula/informed_disciplines/${id}`, data)
    .then((response) => response);

export const fetchDispensaDisciplina = (params) =>
  axios
    .get(`${API_HOST}/curricula/informed_disciplines`, { params })
    .then((response) => response.data);

export const getDispensaDisciplina = (id) =>
  axios
    .get(`${API_HOST}/curricula/informed_disciplines/${id}`)
    .then((response) => response.data);

export const deleteDispensaDisciplina = (id) =>
  axios
    .delete(`${API_HOST}/curricula/informed_disciplines/${id}`)
    .then((response) => response.data);

export const editGrid = (curriculumId, id, data) =>
  axios
    .patch(`${API_HOST}/curricula/grids/${id}`, data)
    .then((response) => response.data);

export const getDisciplineRequirements = () =>
  axios
    .get(`${API_HOST}/curricula/discipline_requirement`)
    .then((res) => res.data);
